import { useEffect } from "react";

/**
 * Minimal SEO helper. It UPDATES existing <meta name="description"> / <link rel="canonical">
 * tags if they exist (so duplicates are never created) and restores everything on unmount.
 * If your project already has an SEO component (react-helmet, etc.), use that instead
 * and delete this hook — see the integration notes.
 */
export default function useDocumentMeta({ title, description, canonical }) {
  useEffect(() => {
    const restore = [];
    const prevTitle = document.title;
    document.title = title;
    restore.push(() => { document.title = prevTitle; });

    const upsert = (selector, tag, attrs, valueAttr, value) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      const prev = el ? el.getAttribute(valueAttr) : null;
      if (!el) {
        el = document.createElement(tag);
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute(valueAttr, value);
      restore.push(() => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute(valueAttr, prev);
      });
    };

    if (description) upsert('meta[name="description"]', "meta", { name: "description" }, "content", description);
    if (canonical) upsert('link[rel="canonical"]', "link", { rel: "canonical" }, "href", canonical);

    return () => restore.reverse().forEach((fn) => fn());
  }, [title, description, canonical]);
}